import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import {
  Package, Wrench, Truck, Plus, Loader2, Trash2, Edit, Search, Box,
  FileSpreadsheet, User, CalendarDays, ArrowLeftRight, MapPin, Fuel,
  Gauge, ChevronDown, ChevronUp, Send, Download, FileText
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const typeConfig = {
  materiale: { icon: Package, color: '#3b82f6', label: 'Materiali' },
  attrezzatura: { icon: Wrench, color: '#f59e0b', label: 'Attrezzature' },
  mezzo: { icon: Truck, color: '#8b5cf6', label: 'Mezzi' },
};

const Magazzino = () => {
  const [items, setItems] = useState([]);
  const [operai, setOperai] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [mezziLog, setMezziLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tutti');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [nI, setNI] = useState({ name: '', type: 'materiale', quantity: '', unit: 'pz', description: '', prezzo_cadauno: '', prezzo_totale: '', provenienza: '', provenienza_nome: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [eD, setED] = useState({});
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importType, setImportType] = useState('materiale');
  const [importing, setImporting] = useState(false);
  const [showConsegna, setShowConsegna] = useState(null);
  const [consegnaData, setConsegnaData] = useState({ consegnato_a: '', data_consegna: '' });
  const [showRiconsegna, setShowRiconsegna] = useState(null);
  const [riconsegnaData, setRiconsegnaData] = useState({ riconsegnato_da: '', data_riconsegna: '' });
  // Mezzi log
  const [expandedMezzo, setExpandedMezzo] = useState(null);
  const [showAddLog, setShowAddLog] = useState(null);
  const [logData, setLogData] = useState({ date: '', km_effettuati: '', carburante_litri: '', costo_carburante: '', note: '' });
  const [editingLog, setEditingLog] = useState(null);
  const [editLogData, setEditLogData] = useState({});
  // Confronto prezzi
  const [showConfronto, setShowConfronto] = useState(false);
  const [confrontoQuery, setConfrontoQuery] = useState('');
  const [confrontoResults, setConfrontoResults] = useState([]);
  const [confrontoLoading, setConfrontoLoading] = useState(false);
  // Invio a cantiere
  const [showInvio, setShowInvio] = useState(null);
  const [invioData, setInvioData] = useState({ cantiereId: '', quantity: '' });
  const pageRef = useRef(null);

  const loadData = async () => {
    try {
      const [i, o, c, ml] = await Promise.all([api.getMagazzino(), api.getOperai(), api.getCantieri(), api.getMezziLog()]);
      setItems(i); setOperai(o); setCantieri(c); setMezziLog(ml);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = items.filter(i => {
    const matchType = activeTab === 'tutti' || i.type === activeTab;
    const matchSearch = !search || i.name.toLowerCase().includes(search.toLowerCase()) || (i.provenienza_nome || '').toLowerCase().includes(search.toLowerCase()) || (i.description || '').toLowerCase().includes(search.toLowerCase());
    return matchType && matchSearch;
  });

  // Auto-calc helper
  const autoCalcTotal = (qty, pCad) => {
    const q = parseFloat(qty) || 0;
    const p = parseFloat(pCad) || 0;
    return (q * p).toFixed(2);
  };

  const handleAdd = async () => {
    if (!nI.name) return;
    const qty = parseFloat(nI.quantity) || 0;
    const pc = parseFloat(nI.prezzo_cadauno) || 0;
    const pt = parseFloat(nI.prezzo_totale) || qty * pc;
    try {
      await api.createMagazzinoItem({ ...nI, quantity: qty, prezzo_cadauno: pc, prezzo_totale: pt });
      setNI({ name: '', type: 'materiale', quantity: '', unit: 'pz', description: '', prezzo_cadauno: '', prezzo_totale: '', provenienza: '', provenienza_nome: '' });
      setShowAdd(false); loadData();
    } catch (e) { console.error(e); }
  };

  const openEdit = (item) => {
    setED({
      name: item.name, type: item.type, quantity: String(item.quantity || ''), unit: item.unit,
      description: item.description || '', prezzo_cadauno: String(item.prezzo_cadauno || ''),
      prezzo_totale: String(item.prezzo_totale || ''), provenienza: item.provenienza || '', provenienza_nome: item.provenienza_nome || ''
    });
    setEditingItem(item);
  };

  const handleSave = async () => {
    if (!editingItem) return;
    const qty = parseFloat(eD.quantity) || 0;
    const pc = parseFloat(eD.prezzo_cadauno) || 0;
    const pt = parseFloat(eD.prezzo_totale) || qty * pc;
    try {
      await api.updateMagazzinoItem(editingItem.id, {
        name: eD.name,
        quantity: qty,
        unit: eD.unit,
        prezzo_cadauno: pc,
        prezzo_totale: pt,
        provenienza: eD.provenienza || '',
        provenienza_nome: eD.provenienza_nome || '',
        description: eD.description || ''
      });
      setEditingItem(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => { try { await api.deleteMagazzinoItem(id); loadData(); } catch (e) { console.error(e); } };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const result = await api.importExcelMagazzino(importType, importFile);
      alert(result.message);
      setShowImport(false); setImportFile(null); loadData();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    setImporting(false);
  };

  const handleConsegna = async () => {
    if (!showConsegna || !consegnaData.consegnato_a) return;
    const w = operai.find(o => o.id === consegnaData.consegnato_a);
    try {
      await api.updateMagazzinoItem(showConsegna.id, {
        consegnato_a: consegnaData.consegnato_a, consegnato_a_nome: w?.name || '',
        data_consegna: consegnaData.data_consegna || new Date().toISOString().split('T')[0],
        data_riconsegna: null, riconsegnato_da: null, riconsegnato_da_nome: null
      });
      setShowConsegna(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleRiconsegna = async () => {
    if (!showRiconsegna || !riconsegnaData.riconsegnato_da) return;
    const w = operai.find(o => o.id === riconsegnaData.riconsegnato_da);
    try {
      await api.updateMagazzinoItem(showRiconsegna.id, {
        data_riconsegna: riconsegnaData.data_riconsegna || new Date().toISOString().split('T')[0],
        riconsegnato_da: riconsegnaData.riconsegnato_da, riconsegnato_da_nome: w?.name || ''
      });
      setShowRiconsegna(null); loadData();
    } catch (e) { console.error(e); }
  };

  // Mezzi log
  const getLogForMezzo = (mId) => mezziLog.filter(l => l.mezzoId === mId).sort((a, b) => b.date.localeCompare(a.date));
  const handleAddLog = async () => {
    if (!showAddLog || !logData.date) return;
    try {
      await api.createMezzoLog({
        mezzoId: showAddLog.id, date: logData.date,
        km_effettuati: parseFloat(logData.km_effettuati) || 0,
        carburante_litri: parseFloat(logData.carburante_litri) || 0,
        costo_carburante: parseFloat(logData.costo_carburante) || 0,
        note: logData.note
      });
      setShowAddLog(null); setLogData({ date: '', km_effettuati: '', carburante_litri: '', costo_carburante: '', note: '' });
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteLog = async (id) => { try { await api.deleteMezzoLog(id); loadData(); } catch (e) { console.error(e); } };

  const openEditLog = (l) => {
    setEditLogData({ date: l.date, km_effettuati: String(l.km_effettuati || ''), carburante_litri: String(l.carburante_litri || ''), costo_carburante: String(l.costo_carburante || ''), note: l.note || '' });
    setEditingLog(l);
  };

  const handleSaveLog = async () => {
    if (!editingLog) return;
    try {
      await api.updateMezzoLog(editingLog.id, {
        date: editLogData.date,
        km_effettuati: parseFloat(editLogData.km_effettuati) || 0,
        carburante_litri: parseFloat(editLogData.carburante_litri) || 0,
        costo_carburante: parseFloat(editLogData.costo_carburante) || 0,
        note: editLogData.note
      });
      setEditingLog(null); loadData();
    } catch (e) { console.error(e); }
  };

  // Confronto prezzi
  const handleConfronto = async () => {
    if (!confrontoQuery.trim()) return;
    setConfrontoLoading(true);
    try {
      const results = await api.confrontaPrezzi(confrontoQuery.trim());
      setConfrontoResults(results);
    } catch (e) { console.error(e); }
    setConfrontoLoading(false);
  };

  // Invio materiale a cantiere
  const handleInvioCantiere = async () => {
    if (!showInvio || !invioData.cantiereId || !invioData.quantity) {
      alert('Seleziona un cantiere e inserisci la quantità');
      return;
    }
    try {
      const result = await api.movimentoMateriale({
        itemId: showInvio.id, action: 'invio_cantiere',
        quantity: parseFloat(invioData.quantity), destCantiereId: invioData.cantiereId
      });
      alert(`${showInvio.name}: ${invioData.quantity} ${showInvio.unit || 'pz'} inviati al cantiere!`);
      setShowInvio(null); setInvioData({ cantiereId: '', quantity: '' });
      loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Errore nello spostamento'); }
  };

  const counts = { materiale: items.filter(i => i.type === 'materiale').length, attrezzatura: items.filter(i => i.type === 'attrezzatura').length, mezzo: items.filter(i => i.type === 'mezzo').length };
  const totalValue = items.reduce((s, i) => s + (i.prezzo_totale || 0), 0);

  const provLabel = (p) => ({ fornitore: 'Fornitore', magazzino: 'Magazzino', cantiere: 'Altro Cantiere' }[p] || p);

  // EXPORT EXCEL
  const exportExcel = () => {
    const data = items.map((item, i) => ({
      'N.': i + 1,
      'Codice': item.id?.slice(0, 8) || '',
      'Tipo': typeConfig[item.type]?.label || item.type,
      'Descrizione': item.name,
      'Unità': item.unit || 'pz',
      'Quantità': item.quantity || 0,
      'Prezzo Cad. (€)': item.prezzo_cadauno || 0,
      'Prezzo Tot. (€)': item.prezzo_totale || 0,
      'Provenienza': item.provenienza_nome || provLabel(item.provenienza) || '',
      'Descrizione Aggiuntiva': item.description || '',
    }));
    // Add totals row
    data.push({ 'N.': '', 'Codice': '', 'Tipo': '', 'Descrizione': 'TOTALE', 'Unità': '', 'Quantità': items.reduce((s, i) => s + (i.quantity || 0), 0), 'Prezzo Cad. (€)': '', 'Prezzo Tot. (€)': totalValue, 'Provenienza': '', 'Descrizione Aggiuntiva': '' });

    const ws = XLSX.utils.json_to_sheet(data);
    // Column widths
    ws['!cols'] = [{ wch: 5 }, { wch: 10 }, { wch: 14 }, { wch: 35 }, { wch: 8 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 25 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Magazzino');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Magazzino_Colabella_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // EXPORT PDF
  const exportPDF = async () => {
    const LOGO = 'https://customer-assets.emergentagent.com/job_colabella-tutorials/artifacts/wcwuz41r_LOGO.png';
    const printDiv = document.createElement('div');
    printDiv.style.cssText = 'position:fixed;top:0;left:0;width:794px;background:#fff;z-index:99999;font-family:Arial,Helvetica,sans-serif;color:#1f2937;padding:0;';

    const rows = items.map((item, i) => `<tr>
      <td style="padding:6px 8px;border:1px solid #ddd;text-align:center;font-size:11px">${i + 1}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px"><span style="display:inline-block;padding:1px 6px;border-radius:3px;font-size:9px;font-weight:700;background:${item.type === 'materiale' ? '#eff6ff;color:#2563eb' : item.type === 'attrezzatura' ? '#fef3c7;color:#d97706' : '#f3e8ff;color:#7c3aed'}">${typeConfig[item.type]?.label || item.type}</span></td>
      <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;font-weight:500">${item.name}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:center">${item.unit || 'pz'}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:right;font-weight:600">${item.quantity || 0}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:right">€${(item.prezzo_cadauno || 0).toFixed(2)}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;font-size:11px;text-align:right;font-weight:700">€${(item.prezzo_totale || 0).toFixed(2)}</td>
      <td style="padding:6px 8px;border:1px solid #ddd;font-size:10px;color:#6b7280">${item.provenienza_nome || provLabel(item.provenienza) || '-'}</td>
    </tr>`).join('');

    printDiv.innerHTML = `
      <div style="background:linear-gradient(135deg,#1a365d,#2563eb);padding:20px 28px;display:flex;align-items:center;justify-content:space-between">
        <div style="display:flex;align-items:center;gap:14px">
          <img src="${LOGO}" style="width:44px;height:44px;border-radius:6px" crossorigin="anonymous"/>
          <div><div style="font-size:20px;font-weight:800;color:#fff;letter-spacing:0.08em">COLABELLA GROUP</div><div style="font-size:10px;color:rgba(255,255,255,0.7);letter-spacing:0.15em">IMPRESA EDILE</div></div>
        </div>
        <div style="text-align:right"><div style="font-size:18px;font-weight:700;color:#fff">Inventario Magazzino</div><div style="font-size:11px;color:rgba(255,255,255,0.7)">${new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</div></div>
      </div>
      <table style="width:100%;border-collapse:collapse;margin-top:4px">
        <tr style="background:#f0f0f0"><td style="padding:8px;border:1px solid #ddd;font-size:10px;font-weight:600;width:30px">N.</td><td style="padding:8px;border:1px solid #ddd;font-size:10px;font-weight:600;width:80px">Tipo</td><td style="padding:8px;border:1px solid #ddd;font-size:10px;font-weight:600">Descrizione</td><td style="padding:8px;border:1px solid #ddd;font-size:10px;font-weight:600;width:40px">U.M.</td><td style="padding:8px;border:1px solid #ddd;font-size:10px;font-weight:600;width:55px;text-align:right">Qtà</td><td style="padding:8px;border:1px solid #ddd;font-size:10px;font-weight:600;width:70px;text-align:right">€/Cad</td><td style="padding:8px;border:1px solid #ddd;font-size:10px;font-weight:600;width:70px;text-align:right">Totale</td><td style="padding:8px;border:1px solid #ddd;font-size:10px;font-weight:600;width:90px">Provenienza</td></tr>
        ${rows}
        <tr style="background:#1a365d;color:#fff"><td colspan="4" style="padding:8px 12px;border:1px solid #1a365d;font-size:12px;font-weight:700">RIEPILOGO</td><td style="padding:8px;border:1px solid #1a365d;font-size:12px;font-weight:700;text-align:right">${items.length} art.</td><td style="padding:8px;border:1px solid #1a365d"></td><td style="padding:8px;border:1px solid #1a365d;font-size:13px;font-weight:800;text-align:right">€${totalValue.toFixed(2)}</td><td style="padding:8px;border:1px solid #1a365d"></td></tr>
      </table>
      <div style="margin-top:10px;display:flex;justify-content:space-between;padding:8px 0">
        <div><span style="font-size:10px;color:#6b7280">Materiali: <strong>${counts.materiale}</strong></span> | <span style="font-size:10px;color:#6b7280">Attrezzature: <strong>${counts.attrezzatura}</strong></span> | <span style="font-size:10px;color:#6b7280">Mezzi: <strong>${counts.mezzo}</strong></span></div>
      </div>
      <div style="margin-top:20px;padding-top:10px;border-top:2px solid #1a365d;font-size:9px;color:#9ca3af;text-align:center"><strong style="color:#1a365d">Colabella Group S.r.l.</strong> — Via Alba 1, 86046 San Martino in Pensilis (CB) — Tel: +39 320 271 1608 — info@colabella.it</div>
    `;
    document.body.appendChild(printDiv);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');
      const canvas = await html2canvas(printDiv, { scale: 2, useCORS: true, backgroundColor: '#fff' });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgW = pdfW - 16;
      const imgH = (canvas.height / canvas.width) * imgW;
      if (imgH <= pdfH - 16) {
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 8, 8, imgW, imgH);
      } else {
        let y = 0, page = 0;
        while (y < imgH) {
          if (page > 0) pdf.addPage();
          const srcY = (y / imgH) * canvas.height;
          const srcH = Math.min(((pdfH - 16) / imgH) * canvas.height, canvas.height - srcY);
          const dH = Math.min(pdfH - 16, imgH - y);
          const pc = document.createElement('canvas');
          pc.width = canvas.width; pc.height = srcH;
          pc.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
          pdf.addImage(pc.toDataURL('image/jpeg', 0.95), 'JPEG', 8, 8, imgW, dH);
          y += pdfH - 16; page++;
        }
      }
      pdf.save(`Magazzino_Colabella_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (e) { alert('Errore generazione PDF'); console.error(e); }
    document.body.removeChild(printDiv);
  };

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  // Reusable price fields with auto-calc
  const PriceFields = ({ qty, setQty, pCad, setPCad, pTot, setPTot }) => (
    <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
      <div className="form-field"><Label>Quantità</Label><Input type="number" value={qty} onChange={e => { const v = e.target.value; setQty(v); setPTot(autoCalcTotal(v, pCad)); }} /></div>
      <div className="form-field"><Label>Prezzo Cad. (€)</Label><Input type="number" step="0.01" value={pCad} onChange={e => { const v = e.target.value; setPCad(v); setPTot(autoCalcTotal(qty, v)); }} /></div>
      <div className="form-field"><Label>Prezzo Tot. (€)</Label><Input type="number" step="0.01" value={pTot} onChange={e => setPTot(e.target.value)} style={{ background: '#f0fdf4', fontWeight: 600 }} /></div>
    </div>
  );

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Magazzino" subtitle={`${items.length} articoli — Valore: €${totalValue.toFixed(2)}`} />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Magazzino</h1>
          <p className="page-subtitle">Materiali, attrezzature e mezzi — Valore: €{totalValue.toFixed(2)}</p>
        </div>
        <div className="header-right">
          <button className="header-action-btn" style={{ background: '#22c55e' }} onClick={exportExcel}><FileSpreadsheet size={16} /><span>Excel</span></button>
          <button className="header-action-btn" style={{ background: '#ef4444' }} onClick={exportPDF}><FileText size={16} /><span>PDF</span></button>
          <SectionActions sectionTitle="Magazzino" contentRef={pageRef} />
        </div>
      </div>

      <div className="mag-action-row">
        <button className="big-action-btn" style={{ flex: 1 }} onClick={() => setShowAdd(true)}><Plus size={20} /> Aggiungi Articolo</button>
        <button className="big-action-btn" style={{ flex: 1, borderColor: '#22c55e', color: '#22c55e' }} onClick={() => setShowImport(true)}><FileSpreadsheet size={20} /> Importa da Excel</button>
        <button className="big-action-btn" style={{ flex: 1, borderColor: '#f59e0b', color: '#f59e0b' }} onClick={() => setShowConfronto(true)}><Search size={20} /> Confronta Prezzi</button>
      </div>

      <div className="mag-stats">
        <div className="mag-stat"><Package size={20} color="#3b82f6" /><span className="mag-stat-val">{counts.materiale}</span><span className="mag-stat-lbl">Materiali</span></div>
        <div className="mag-stat"><Wrench size={20} color="#f59e0b" /><span className="mag-stat-val">{counts.attrezzatura}</span><span className="mag-stat-lbl">Attrezzature</span></div>
        <div className="mag-stat"><Truck size={20} color="#8b5cf6" /><span className="mag-stat-val">{counts.mezzo}</span><span className="mag-stat-lbl">Mezzi</span></div>
      </div>

      <div className="mag-filters">
        <div className="search-bar" style={{ maxWidth: '320px' }}><Search size={18} className="search-icon" /><Input placeholder="Cerca materiale, fornitore..." value={search} onChange={e => setSearch(e.target.value)} className="search-input" /></div>
        <div className="cantiere-filter">
          {['tutti', 'materiale', 'attrezzatura', 'mezzo'].map(t => (
            <button key={t} className={`filter-chip ${activeTab === t ? 'filter-chip-active' : ''}`} onClick={() => setActiveTab(t)}>
              {t === 'tutti' ? `Tutti (${items.length})` : `${typeConfig[t]?.label} (${counts[t]})`}
            </button>
          ))}
        </div>
      </div>

      <div className="mag-grid">
        {filtered.map(item => {
          const cfg = typeConfig[item.type] || typeConfig.materiale;
          const Icon = cfg.icon;
          const isMezzo = item.type === 'mezzo';
          const isAttr = item.type === 'attrezzatura';
          const logs = isMezzo ? getLogForMezzo(item.id) : [];
          const isExpMezzo = expandedMezzo === item.id;
          const totalKm = logs.reduce((s, l) => s + (l.km_effettuati || 0), 0);
          const totalFuel = logs.reduce((s, l) => s + (l.costo_carburante || 0), 0);

          return (
            <div key={item.id} className={`mag-card-wrap ${isExpMezzo ? 'mag-card-expanded' : ''}`}>
              <div className="mag-card">
                <div className="mag-card-icon" style={{ background: `${cfg.color}15`, color: cfg.color }}><Icon size={22} /></div>
                <div className="mag-card-info">
                  <h4>{item.name}</h4>
                  <p className="mag-card-type">{cfg.label} {item.description && ` — ${item.description}`}</p>
                  {(item.prezzo_cadauno > 0 || item.prezzo_totale > 0) && (
                    <p className="mag-card-price">€{(item.prezzo_cadauno || 0).toFixed(2)}/cad — Totale: <strong>€{(item.prezzo_totale || 0).toFixed(2)}</strong></p>
                  )}
                  {item.provenienza && (
                    <p className="mag-prov"><MapPin size={11} /> {provLabel(item.provenienza)}{item.provenienza_nome && `: ${item.provenienza_nome}`}</p>
                  )}
                  {isAttr && item.consegnato_a_nome && (
                    <p className="mag-consegna-info"><User size={12} /> Consegnato a: <strong>{item.consegnato_a_nome}</strong> il {item.data_consegna || '-'}
                      {item.data_riconsegna ? <span className="mag-riconsegna"> — Riconsegnato il {item.data_riconsegna} da {item.riconsegnato_da_nome}</span> : <span className="mag-in-uso"> — IN USO</span>}
                    </p>
                  )}
                  {isMezzo && logs.length > 0 && (
                    <p className="mag-mezzo-summary"><Gauge size={11} /> Totale: {totalKm.toFixed(0)} km — €{totalFuel.toFixed(2)} carburante</p>
                  )}
                </div>
                <div className="mag-card-qty"><span className="mag-qty-val">{item.quantity}</span><span className="mag-qty-unit">{item.unit}</span></div>
                <div className="mag-card-actions">
                  {item.type === 'materiale' && item.quantity > 0 && (
                    <button className="mag-action-btn-sm" style={{ color: '#3b82f6' }} onClick={() => { setShowInvio(item); setInvioData({ cantiereId: '', quantity: '' }); }} title="Invia a Cantiere"><Send size={13} /></button>
                  )}
                  {isAttr && <button className="mag-action-btn-sm" style={{ color: '#22c55e' }} onClick={() => { setShowConsegna(item); setConsegnaData({ consegnato_a: '', data_consegna: new Date().toISOString().split('T')[0] }); }} title="Consegna"><ArrowLeftRight size={13} /></button>}
                  {isAttr && item.consegnato_a && !item.data_riconsegna && (
                    <button className="mag-action-btn-sm" style={{ color: '#3b82f6' }} onClick={() => { setShowRiconsegna(item); setRiconsegnaData({ riconsegnato_da: '', data_riconsegna: new Date().toISOString().split('T')[0] }); }} title="Riconsegna"><CalendarDays size={13} /></button>
                  )}
                  {isMezzo && <button className="mag-action-btn-sm" style={{ color: '#8b5cf6' }} onClick={() => setExpandedMezzo(isExpMezzo ? null : item.id)} title="Log Giornaliero">{isExpMezzo ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</button>}
                  {isMezzo && <button className="mag-action-btn-sm" style={{ color: '#22c55e' }} onClick={() => { setShowAddLog(item); setLogData({ date: new Date().toISOString().split('T')[0], km_effettuati: '', carburante_litri: '', costo_carburante: '', note: '' }); }} title="Aggiungi Log"><Plus size={13} /></button>}
                  <button className="mag-action-btn-sm" onClick={() => openEdit(item)}><Edit size={13} /></button>
                  <button className="mag-action-btn-sm" style={{ color: '#d1d5db' }} onClick={() => handleDelete(item.id)}><Trash2 size={13} /></button>
                </div>
              </div>
              {/* Mezzi expanded log */}
              {isMezzo && isExpMezzo && (
                <div className="mezzo-log-section">
                  <div className="mezzo-log-header"><h5>Log Giornaliero — {item.name}</h5></div>
                  <div className="mezzo-log-table">
                    <table>
                      <thead><tr><th>Data</th><th>Km</th><th>Litri</th><th>€ Carburante</th><th>Note</th><th></th></tr></thead>
                      <tbody>
                        {logs.map(l => (
                          <tr key={l.id}>
                            <td>{new Date(l.date + 'T12:00').toLocaleDateString('it-IT')}</td>
                            <td><strong>{l.km_effettuati}</strong> km</td>
                            <td>{l.carburante_litri} lt</td>
                            <td>€{(l.costo_carburante || 0).toFixed(2)}</td>
                            <td style={{ color: '#9ca3af', fontSize: 12 }}>{l.note || '-'}</td>
                            <td>
                              <div style={{ display: 'flex', gap: 3 }}>
                                <button className="pa-edit" onClick={() => openEditLog(l)}><Edit size={12} /></button>
                                <button className="pa-delete" onClick={() => handleDeleteLog(l.id)}><Trash2 size={12} /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {logs.length === 0 && <p className="empty-text">Nessun log registrato</p>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div className="empty-cantieri"><Box size={48} color="#d1d5db" /><h3>Magazzino vuoto</h3><p>Aggiungi articoli o importa da Excel</p></div>}
      </div>

      {/* Add Dialog */}
      {showAdd && (
        <Dialog open={showAdd} onOpenChange={setShowAdd}><DialogContent>
          <DialogHeader><DialogTitle>Aggiungi al Magazzino</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-field"><Label>Nome *</Label><Input value={nI.name} onChange={e => setNI({...nI, name: e.target.value})} placeholder="Es: Cemento Portland" /></div>
            <div className="form-row">
              <div className="form-field"><Label>Tipo</Label>
                <Select value={nI.type} onValueChange={v => setNI({...nI, type: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="materiale">Materiale</SelectItem><SelectItem value="attrezzatura">Attrezzatura</SelectItem><SelectItem value="mezzo">Mezzo</SelectItem></SelectContent></Select></div>
              <div className="form-field"><Label>Unità</Label><Input value={nI.unit} onChange={e => setNI({...nI, unit: e.target.value})} placeholder="pz, kg, m..." /></div>
            </div>
            <PriceFields
              qty={nI.quantity} setQty={v => setNI(p => ({...p, quantity: v}))}
              pCad={nI.prezzo_cadauno} setPCad={v => setNI(p => ({...p, prezzo_cadauno: v}))}
              pTot={nI.prezzo_totale} setPTot={v => setNI(p => ({...p, prezzo_totale: v}))}
            />
            <div className="form-row">
              <div className="form-field"><Label>Provenienza</Label>
                <Select value={nI.provenienza} onValueChange={v => setNI({...nI, provenienza: v})}><SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent><SelectItem value="fornitore">Fornitore</SelectItem><SelectItem value="magazzino">Magazzino</SelectItem><SelectItem value="cantiere">Altro Cantiere</SelectItem></SelectContent></Select></div>
              <div className="form-field"><Label>Nome Provenienza</Label><Input value={nI.provenienza_nome} onChange={e => setNI({...nI, provenienza_nome: e.target.value})} placeholder="Es: Leroy Merlin, Cantiere Roma..." /></div>
            </div>
            <div className="form-field"><Label>Descrizione</Label><Input value={nI.description} onChange={e => setNI({...nI, description: e.target.value})} /></div>
            <button className="form-submit" onClick={handleAdd} disabled={!nI.name}>Aggiungi</button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Edit Dialog */}
      {editingItem && (
        <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}><DialogContent>
          <DialogHeader><DialogTitle>Modifica: {editingItem.name}</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-field"><Label>Nome</Label><Input value={eD.name || ''} onChange={e => setED({...eD, name: e.target.value})} /></div>
            <div className="form-row"><div className="form-field"><Label>Unità</Label><Input value={eD.unit || ''} onChange={e => setED({...eD, unit: e.target.value})} /></div></div>
            <PriceFields
              qty={eD.quantity || ''} setQty={v => setED(p => ({...p, quantity: v}))}
              pCad={eD.prezzo_cadauno || ''} setPCad={v => setED(p => ({...p, prezzo_cadauno: v}))}
              pTot={eD.prezzo_totale || ''} setPTot={v => setED(p => ({...p, prezzo_totale: v}))}
            />
            <div className="form-row">
              <div className="form-field"><Label>Provenienza</Label>
                <Select value={eD.provenienza || ''} onValueChange={v => setED({...eD, provenienza: v})}><SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent><SelectItem value="fornitore">Fornitore</SelectItem><SelectItem value="magazzino">Magazzino</SelectItem><SelectItem value="cantiere">Altro Cantiere</SelectItem></SelectContent></Select></div>
              <div className="form-field"><Label>Nome Provenienza</Label><Input value={eD.provenienza_nome || ''} onChange={e => setED({...eD, provenienza_nome: e.target.value})} /></div>
            </div>
            <button className="form-submit" onClick={handleSave}>Salva</button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Import Excel */}
      {showImport && (
        <Dialog open={showImport} onOpenChange={setShowImport}><DialogContent>
          <DialogHeader><DialogTitle>Importa da Excel</DialogTitle></DialogHeader>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>Colonne: <strong>Descrizione, Unità, Quantità, Prezzo Cadauno, Prezzo Totale, Fornitore</strong> (la colonna Fornitore è opzionale)</p>
          <div className="form-fields">
            <div className="form-field"><Label>Tipo</Label><Select value={importType} onValueChange={setImportType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="materiale">Materiali</SelectItem><SelectItem value="attrezzatura">Attrezzature</SelectItem><SelectItem value="mezzo">Mezzi</SelectItem></SelectContent></Select></div>
            <div className="form-field"><Label>File Excel *</Label><input type="file" accept=".xlsx,.xls" onChange={e => setImportFile(e.target.files[0])} className="form-file-input" /></div>
            <button className="form-submit" onClick={handleImport} disabled={!importFile || importing}>{importing ? 'Importazione...' : 'Importa'}</button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Consegna */}
      {showConsegna && (<Dialog open={!!showConsegna} onOpenChange={() => setShowConsegna(null)}><DialogContent>
        <DialogHeader><DialogTitle>Consegna: {showConsegna.name}</DialogTitle></DialogHeader>
        <div className="form-fields">
          <div className="form-field"><Label>Consegnato a *</Label><Select value={consegnaData.consegnato_a} onValueChange={v => setConsegnaData({...consegnaData, consegnato_a: v})}><SelectTrigger><SelectValue placeholder="Operaio" /></SelectTrigger><SelectContent>{operai.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="form-field"><Label>Data</Label><Input type="date" value={consegnaData.data_consegna} onChange={e => setConsegnaData({...consegnaData, data_consegna: e.target.value})} /></div>
          <button className="form-submit" onClick={handleConsegna} disabled={!consegnaData.consegnato_a}>Registra</button>
        </div></DialogContent></Dialog>)}

      {/* Riconsegna */}
      {showRiconsegna && (<Dialog open={!!showRiconsegna} onOpenChange={() => setShowRiconsegna(null)}><DialogContent>
        <DialogHeader><DialogTitle>Riconsegna: {showRiconsegna.name}</DialogTitle></DialogHeader>
        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 8px' }}>In uso da: <strong>{showRiconsegna.consegnato_a_nome}</strong></p>
        <div className="form-fields">
          <div className="form-field"><Label>Riconsegnato da *</Label><Select value={riconsegnaData.riconsegnato_da} onValueChange={v => setRiconsegnaData({...riconsegnaData, riconsegnato_da: v})}><SelectTrigger><SelectValue placeholder="Operaio" /></SelectTrigger><SelectContent>{operai.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="form-field"><Label>Data</Label><Input type="date" value={riconsegnaData.data_riconsegna} onChange={e => setRiconsegnaData({...riconsegnaData, data_riconsegna: e.target.value})} /></div>
          <button className="form-submit" onClick={handleRiconsegna} disabled={!riconsegnaData.riconsegnato_da}>Registra</button>
        </div></DialogContent></Dialog>)}

      {/* Add Mezzo Log */}
      {showAddLog && (<Dialog open={!!showAddLog} onOpenChange={() => setShowAddLog(null)}><DialogContent>
        <DialogHeader><DialogTitle>Log Giornaliero — {showAddLog.name}</DialogTitle></DialogHeader>
        <div className="form-fields">
          <div className="form-field"><Label>Data *</Label><Input type="date" value={logData.date} onChange={e => setLogData({...logData, date: e.target.value})} /></div>
          <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            <div className="form-field"><Label>Km Effettuati</Label><Input type="number" value={logData.km_effettuati} onChange={e => setLogData({...logData, km_effettuati: e.target.value})} placeholder="0" /></div>
            <div className="form-field"><Label>Carburante (litri)</Label><Input type="number" step="0.1" value={logData.carburante_litri} onChange={e => setLogData({...logData, carburante_litri: e.target.value})} placeholder="0" /></div>
            <div className="form-field"><Label>Costo Carburante (€)</Label><Input type="number" step="0.01" value={logData.costo_carburante} onChange={e => setLogData({...logData, costo_carburante: e.target.value})} placeholder="0.00" /></div>
          </div>
          <div className="form-field"><Label>Note</Label><Input value={logData.note} onChange={e => setLogData({...logData, note: e.target.value})} placeholder="Es: Viaggio cantiere Roma" /></div>
          <button className="form-submit" onClick={handleAddLog} disabled={!logData.date}>Registra</button>
        </div></DialogContent></Dialog>)}

      {/* Edit Mezzo Log Dialog */}
      {editingLog && (
        <Dialog open={!!editingLog} onOpenChange={() => setEditingLog(null)}><DialogContent>
          <DialogHeader><DialogTitle>Modifica Log</DialogTitle></DialogHeader>
          <div className="form-fields">
            <div className="form-field"><Label>Data</Label><Input type="date" value={editLogData.date} onChange={e => setEditLogData(p => ({...p, date: e.target.value}))} /></div>
            <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div className="form-field"><Label>Km</Label><Input type="number" value={editLogData.km_effettuati} onChange={e => setEditLogData(p => ({...p, km_effettuati: e.target.value}))} /></div>
              <div className="form-field"><Label>Litri</Label><Input type="number" step="0.1" value={editLogData.carburante_litri} onChange={e => setEditLogData(p => ({...p, carburante_litri: e.target.value}))} /></div>
              <div className="form-field"><Label>€ Carburante</Label><Input type="number" step="0.01" value={editLogData.costo_carburante} onChange={e => setEditLogData(p => ({...p, costo_carburante: e.target.value}))} /></div>
            </div>
            <div className="form-field"><Label>Note</Label><Input value={editLogData.note || ''} onChange={e => setEditLogData(p => ({...p, note: e.target.value}))} /></div>
            <button className="form-submit" onClick={handleSaveLog}>Salva Modifiche</button>
          </div>
        </DialogContent></Dialog>
      )}

      {/* Confronta Prezzi Dialog */}
      {showConfronto && (
        <Dialog open={showConfronto} onOpenChange={setShowConfronto}><DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Confronta Prezzi Fornitori</DialogTitle></DialogHeader>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
            Cerca un materiale per confrontare i prezzi tra diversi fornitori e trovare il più conveniente.
          </p>
          <div className="confronto-search-row">
            <Input
              value={confrontoQuery}
              onChange={e => setConfrontoQuery(e.target.value)}
              placeholder="Es: cemento, ferro, mattoni..."
              onKeyDown={e => e.key === 'Enter' && handleConfronto()}
            />
            <button className="form-submit" style={{ minWidth: 100 }} onClick={handleConfronto} disabled={confrontoLoading}>
              {confrontoLoading ? 'Cerco...' : 'Cerca'}
            </button>
          </div>

          {confrontoResults.length > 0 && (
            <div className="confronto-results">
              <div className="confronto-best">
                <span className="confronto-best-label">Miglior Prezzo:</span>
                <span className="confronto-best-name">{confrontoResults[0].name}</span>
                <span className="confronto-best-price">€{(confrontoResults[0].prezzo_cadauno || 0).toFixed(2)}/{confrontoResults[0].unit}</span>
                {confrontoResults[0].provenienza_nome && <span className="confronto-best-prov">da {confrontoResults[0].provenienza_nome}</span>}
              </div>
              <table className="confronto-table">
                <thead><tr><th>#</th><th>Articolo</th><th>Fornitore</th><th>€/Cad</th><th>€ Totale</th><th>Qtà</th><th>Risparmio</th></tr></thead>
                <tbody>
                  {confrontoResults.map((item, i) => {
                    const bestPrice = confrontoResults[0].prezzo_cadauno || 0;
                    const diff = (item.prezzo_cadauno || 0) - bestPrice;
                    return (
                      <tr key={item.id} className={i === 0 ? 'confronto-best-row' : ''}>
                        <td>{i === 0 ? '⭐' : i + 1}</td>
                        <td><strong>{item.name}</strong></td>
                        <td>{item.provenienza_nome || item.provenienza || '-'}</td>
                        <td style={{ fontWeight: 700 }}>€{(item.prezzo_cadauno || 0).toFixed(2)}</td>
                        <td>€{(item.prezzo_totale || 0).toFixed(2)}</td>
                        <td>{item.quantity} {item.unit}</td>
                        <td style={{ color: diff === 0 ? '#22c55e' : '#ef4444' }}>
                          {diff === 0 ? 'MIGLIORE' : `+€${diff.toFixed(2)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {confrontoResults.length === 1 && (
                <p style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center', marginTop: 12 }}>
                  Solo 1 fornitore trovato. Aggiungi lo stesso materiale da altri fornitori per confrontare.
                </p>
              )}
            </div>
          )}
          {confrontoResults.length === 0 && confrontoQuery && !confrontoLoading && (
            <p style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: 20 }}>
              Nessun risultato per "{confrontoQuery}". Prova con un altro termine.
            </p>
          )}
        </DialogContent></Dialog>
      )}

      {/* Invio a Cantiere Dialog */}
      {showInvio && (
        <Dialog open={!!showInvio} onOpenChange={() => setShowInvio(null)}><DialogContent>
          <DialogHeader><DialogTitle>Invia a Cantiere — {showInvio.name}</DialogTitle></DialogHeader>
          <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
            Disponibili in magazzino: <strong>{showInvio.quantity} {showInvio.unit}</strong> (€{(showInvio.prezzo_cadauno || 0).toFixed(2)}/cad)
          </p>
          <div className="form-fields">
            <div className="form-field"><Label>Cantiere di destinazione *</Label>
              <Select value={invioData.cantiereId} onValueChange={v => setInvioData(prev => ({...prev, cantiereId: v}))}>
                <SelectTrigger><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="form-field"><Label>Quantità da inviare *</Label>
              <Input type="number" step="0.5" max={showInvio.quantity} value={invioData.quantity} onChange={e => setInvioData(prev => ({...prev, quantity: e.target.value}))} placeholder={`Max: ${showInvio.quantity}`} /></div>
            {invioData.quantity && <p style={{ fontSize: '13px', color: '#3b82f6', margin: '0' }}>
              Valore: €{(parseFloat(invioData.quantity || 0) * (showInvio.prezzo_cadauno || 0)).toFixed(2)} — Rimanenza magazzino: {(showInvio.quantity - parseFloat(invioData.quantity || 0)).toFixed(0)} {showInvio.unit}
            </p>}
            <button className="form-submit" onClick={handleInvioCantiere} disabled={!invioData.cantiereId || !invioData.quantity}>Invia a Cantiere</button>
          </div>
        </DialogContent></Dialog>
      )}
    </div>
  );
};

export default Magazzino;
